import React, { useCallback, useEffect, useState } from 'react';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import { SnackbarSeverityType } from '../../enums';
import { Box, CircularProgress } from '@mui/material';
import DropdownSelect from '../../components/DropdownSelect';
import { RoundedSearchButton, SearchContainer } from './styled';
import { getAllColumnsMappingOptions } from './config/constant';
import studentProfile from '../../apis/studentProfile';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import StudentProfilePageTable from './components/StudentProfilePageTable';
import StudentDetailPage from './StudentDetailPage';
import { Dictionary, isEmpty } from 'lodash';
import {
  fetchAllStudentReportPages,
  getTransformedStudentData
} from './utils/studentReportUtils';
import { transformStudentDetailData } from '../../utils/studentDataTransformer';
import TextInputWithIcon from '../../components/TextInputWithIcon';
import { downloadExcelFromJson } from '../../utils/fileGeneration';
import { PageWrapper, Title } from '../common/styled';
import Icons from '../../components/icons';
import SelectAll from '../../components/SelectAllField/SelectAllField';

interface StudentProfilePageProps {
  allClasses?: SchoolClassesConfig;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const StudentProfilePage: React.FC<StudentProfilePageProps> = ({
  allClasses,
  onShowToast
}) => {
  const { user: loggedInUser } = useSelector((state: RootState) => state.profile);

  const columnsToExcludeInDropDown = [
    'mother__profile',
    'father__profile',
    'guardian__profile'
  ];
  const filteredColumns = getAllColumnsMappingOptions(loggedInUser?.school || '').filter(
    (column) => !columnsToExcludeInDropDown.includes(column.value)
  );
  const [selectedGrade, setSelectedGrade] = useState('All');
  const [selectedDivision, setSelectedDivision] = useState('All');
  const [divisionOptions, setDivisionOptions] = useState<string[]>([]);

  const [studentProfileData, setStudentProfileData] =
    useState<Dictionary<string | undefined>[]>();
  const [studentDetailData, setStudentDetailData] = useState<any>();

  const [studentName, setStudentName] = useState('');
  const [enrollmentNo, setEnrollmentNo] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    filteredColumns.map((column) => column.value)
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [studentDetailModal, setStudentDetailModal] = useState(false);

  useEffect(() => {
    if (!allClasses) return;

    const divisions =
      selectedGrade && allClasses.classes
        ? allClasses.classes
          .filter(({ grade }) => grade === selectedGrade)
          .map(({ division }) => division)
        : allClasses.divisions || [];

    setDivisionOptions(divisions);
  }, [allClasses, selectedGrade]);

  const handleGradeSearchChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    setSelectedGrade(event.target.value as string);
    setSelectedDivision('All');
    setDivisionOptions([]);
    setIsSearched(false);
  };

  const fetchStudentReportData = useCallback(
    async (isDownload: boolean) => {
      const params = {
        selectedColumns,
        name: studentName,
        enrollmentNo,
        grade: selectedGrade === 'All' ? '' : selectedGrade,
        division: selectedDivision === 'All' ? '' : selectedDivision
      };

      try {
        const usersData = await fetchAllStudentReportPages(params);
        return getTransformedStudentData(
          usersData,
          isDownload,
          selectedColumns,
          loggedInUser?.school || ''
        );
      } catch (error: any) {
        onShowToast(
          error?.errors?.[0].message || 'Error getting student report',
          SnackbarSeverityType.error
        );
        return [];
      }
    },
    [
      selectedColumns,
      studentName,
      enrollmentNo,
      selectedGrade,
      selectedDivision,
      onShowToast,
      loggedInUser?.school
    ]
  );

  const handleSearch = async () => {
    setIsSubmitting(true);
    setIsSearched(false);
    const data = await fetchStudentReportData(false);
    setStudentProfileData(data);
    setIsSubmitting(false);
    setIsSearched(true);
  };

  const handleDownload = async () => {
    setIsSubmitting(true);
    const data = await fetchStudentReportData(true);
    if (isEmpty(data)) {
      onShowToast('No Data to download', SnackbarSeverityType.error);
      return;
    }
    const headersSet = new Set<string>();
    data.forEach((row: { [key: string]: any }) => {
      Object.keys(row).forEach((key) => headersSet.add(key));
    });
    const headers = Array.from(headersSet);
    const body = data.map((row: { [key: string]: any }) =>
      headers.map((header) => row[header])
    );
    downloadExcelFromJson('student_report', 'Student Report', {
      headers,
      body
    });
    setIsSubmitting(false);
  };

  const getStudentDetail = useCallback(
    async (studentId: string) => {
      if (!studentId) return;
      try {
        const params = {
          studentIds: [studentId]
        };
        const response = await studentProfile.fetchStudentDetailApi(params);

        const transformedStudentDetailData =
          transformStudentDetailData(response);
        setStudentDetailData(transformedStudentDetailData);
      } catch (error: any) {
        onShowToast(
          error.errors?.[0].message || 'Error getting student detail',
          SnackbarSeverityType.error
        );
      }
    },
    [onShowToast, setStudentDetailData]
  );

  const handleRowEdit = async (row: any) => {
    await getStudentDetail(row.id);
    setStudentDetailModal(!studentDetailModal);
  };

  const gradeOption = [
    { value: 'All', label: 'All' },
    ...(allClasses?.grades?.map((g) => ({ value: g, label: g })) ?? [])
  ];

  const divisionOption = [
    { value: 'All', label: 'All' },
    ...divisionOptions.map((d) => ({ value: d, label: d }))
  ];

  return (
    <>
      <Title>Student Profile Details</Title>
      {!studentDetailModal && (
        <Box>
          <PageWrapper sx={{ minHeight: 'auto !important' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                gap: 2,
                alignItems: 'end'
              }}
            >
              <SearchContainer>
                <TextInputWithIcon
                  sx={{
                    '& .css-1avgao2-MuiInputBase-root-MuiOutlinedInput-root': {
                      minWidth: '170px !important'
                    }
                  }}
                  fullWidth
                  label='Student Name'
                  required={false}
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  variant='outlined'
                  placeholder='Search by Student Name'
                />

                <TextInputWithIcon
                  sx={{
                    '& .css-1avgao2-MuiInputBase-root-MuiOutlinedInput-root': {
                      minWidth: '170px !important'
                    }
                  }}
                  fullWidth
                  label='Enrollment Number'
                  required={false}
                  value={enrollmentNo}
                  onChange={(e) => setEnrollmentNo(e.target.value)}
                  variant='outlined'
                  placeholder='Enter Full Enrollment Number'
                />

                <DropdownSelect
                  sx={{
                    minWidth: '86px !important'
                  }}
                  label='Grade'
                  required={false}
                  placeholder='Select Grade'
                  name='grade'
                  value={selectedGrade}
                  options={gradeOption}
                  hasTopLabel
                  onChange={handleGradeSearchChange}
                />
                <DropdownSelect
                  sx={{
                    minWidth: '86px !important'
                  }}
                  label='Division'
                  required={false}
                  placeholder='Select Division'
                  name='division'
                  value={selectedDivision}
                  options={divisionOption}
                  onChange={(e) => {
                    setSelectedDivision(e.target.value as string);
                    setIsSearched(false);
                  }}
                  hasTopLabel
                  disabled={!selectedGrade}
                />

                <SelectAll
                  sx={{
                    minWidth: '231px !important',
                    maxWidth: '231px !important'
                  }}
                  label='Select Columns'
                  placeholder='Select Columns'
                  onChange={(value) => setSelectedColumns(value)}
                  options={filteredColumns}
                  selected={selectedColumns}
                  hasTopLabel
                />
              </SearchContainer>
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  flexDirection: 'column',
                  flex: 1,
                  '@media (min-width:600px)': {
                    flexDirection: 'row',
                    flex: 0
                  }
                }}
              >
                <RoundedSearchButton
                  onClick={handleSearch}
                  disabled={isSubmitting}
                  sx={{ flexShrink: 0, marginTop: '24px !important' }}
                >
                  <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
                  {isSubmitting ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Search'
                  )}
                </RoundedSearchButton>
                <RoundedSearchButton
                  onClick={handleDownload}
                  disabled={isSubmitting}
                  sx={{ flexShrink: 0, marginTop: '24px !important' }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Download'
                  )}
                </RoundedSearchButton>
              </Box>
            </Box>
          </PageWrapper>
          {isSearched && (
            <StudentProfilePageTable
              isLoading={isSubmitting}
              selectedColumns={selectedColumns}
              studentProfileData={studentProfileData}
              onEdit={handleRowEdit}
              school={loggedInUser?.school || ''}
            />
          )}
        </Box>
      )}

      {studentDetailModal && (
        <StudentDetailPage
          studentParentProfileData={studentDetailData}
          handleRowEdit={handleRowEdit}
        />
      )}
    </>
  );
};

export default StudentProfilePage;
